<script>
import { useAlert } from 'dashboard/composables';
import { format, parseISO } from 'date-fns';
import SectionCard from '../SectionCard.vue';
import ScheduleModal from '../modals/ScheduleModal.vue';
import DateOverrideModal from '../modals/DateOverrideModal.vue';
import DeleteModal from 'dashboard/components/dahreply/DeleteModal.vue';
import { hoursText } from '../helpers';
import {
  listSchedules,
  deleteSchedule,
  listScheduleOverrides,
  createScheduleOverride,
  deleteScheduleOverride,
} from '../../../../api/realEstate/schedules';
import { apiErrorMessage } from '../../../../api/realEstate/errors';

// Layer 3 — regular agent schedules — fetched directly here (not Vuex),
// same single-screen approach BookingSection's special dates already take
// (docs/real-estate/be/10-api-reference.md §A8). Each card lazy-loads its
// own overrides in a separate request once the schedule list is in, since
// the list endpoint doesn't include them.
export default {
  name: 'SchedulesSection',
  components: { SectionCard, ScheduleModal, DateOverrideModal, DeleteModal },
  props: {
    project: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      schedules: [],
      isLoading: false,
      overridesByScheduleId: {},
      isModalOpen: false,
      editingSchedule: null,
      scheduleToDelete: null,
      isDeletingSchedule: false,
      addOverrideScheduleId: null,
      overrideToDelete: null,
      isDeletingOverride: false,
    };
  },
  watch: {
    'project.property_id': {
      immediate: true,
      handler() {
        this.loadSchedules();
      },
    },
  },
  methods: {
    async loadSchedules() {
      this.isLoading = true;
      try {
        const { data } = await listSchedules(this.project.property_id);
        this.schedules = data.results;
        this.schedules.forEach(schedule =>
          this.loadOverridesFor(schedule.schedule_id)
        );
      } catch (err) {
        useAlert(
          apiErrorMessage(this, err, 'REAL_ESTATE.SCHEDULES.ERROR_LOAD')
        );
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        this.isLoading = false;
      }
    },
    async loadOverridesFor(scheduleId) {
      const existing = this.overridesByScheduleId[scheduleId];
      this.$set(this.overridesByScheduleId, scheduleId, {
        items: existing ? existing.items : [],
        isLoading: true,
      });
      try {
        const { data } = await listScheduleOverrides(scheduleId);
        this.$set(this.overridesByScheduleId, scheduleId, {
          items: data.results
            .slice()
            .sort((a, b) => (a.override_date < b.override_date ? -1 : 1)),
          isLoading: false,
        });
      } catch (err) {
        this.$set(this.overridesByScheduleId, scheduleId, {
          items: [],
          isLoading: false,
        });
        useAlert(
          apiErrorMessage(
            this,
            err,
            'REAL_ESTATE.SCHEDULES.ERROR_LOAD_OVERRIDES'
          )
        );
        // eslint-disable-next-line no-console
        console.error(err);
      }
    },
    hoursTextFor(workingHours) {
      return hoursText(workingHours || []);
    },
    overridesFor(scheduleId) {
      const entry = this.overridesByScheduleId[scheduleId];
      return entry ? entry.items : [];
    },
    isLoadingOverridesFor(scheduleId) {
      const entry = this.overridesByScheduleId[scheduleId];
      return entry ? entry.isLoading : true;
    },
    formatDate(d) {
      return format(parseISO(d), 'd MMM yyyy');
    },
    openCreateModal() {
      this.editingSchedule = null;
      this.isModalOpen = true;
    },
    openEditModal(schedule) {
      this.editingSchedule = schedule;
      this.isModalOpen = true;
    },
    closeModal() {
      this.isModalOpen = false;
      this.editingSchedule = null;
    },
    handleSaved() {
      this.closeModal();
      this.loadSchedules();
    },
    async confirmDeleteSchedule() {
      this.isDeletingSchedule = true;
      try {
        await deleteSchedule(this.scheduleToDelete.schedule_id);
        useAlert(this.$t('REAL_ESTATE.SCHEDULES.SUCCESS_DELETED'));
        this.scheduleToDelete = null;
        await this.loadSchedules();
      } catch (err) {
        useAlert(
          apiErrorMessage(this, err, 'REAL_ESTATE.SCHEDULES.ERROR_DELETE')
        );
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        this.isDeletingSchedule = false;
      }
    },
    async handleSaveOverride(scheduleId, body) {
      await createScheduleOverride(scheduleId, body);
      await this.loadOverridesFor(scheduleId);
    },
    async confirmDeleteOverride() {
      const { scheduleId, override } = this.overrideToDelete;
      this.isDeletingOverride = true;
      try {
        await deleteScheduleOverride(scheduleId, override.id);
        useAlert(this.$t('REAL_ESTATE.OVERRIDES.SUCCESS_DELETED'));
        this.overrideToDelete = null;
        await this.loadOverridesFor(scheduleId);
      } catch (err) {
        useAlert(
          apiErrorMessage(this, err, 'REAL_ESTATE.OVERRIDES.ERROR_DELETE')
        );
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        this.isDeletingOverride = false;
      }
    },
  },
};
</script>

<template>
  <SectionCard
    section-id="schedules"
    :title="$t('REAL_ESTATE.SCHEDULES.TITLE')"
  >
    <template #action>
      <woot-button
        variant="clear"
        size="small"
        icon="add"
        @click="openCreateModal"
      >
        {{ $t('REAL_ESTATE.SCHEDULES.ADD_BUTTON') }}
      </woot-button>
    </template>

    <ScheduleModal
      v-if="isModalOpen"
      :project="project"
      :schedule="editingSchedule"
      @close="closeModal"
      @saved="handleSaved"
    />
    <DeleteModal
      v-if="scheduleToDelete"
      :delete-name="scheduleToDelete.name"
      :is-loading="isDeletingSchedule"
      @confirm="confirmDeleteSchedule"
      @close="scheduleToDelete = null"
    >
      <p class="mt-2 mb-0 text-sm text-slate-500 dark:text-slate-400">
        {{ $t('REAL_ESTATE.SCHEDULES.DELETE_NOTE') }}
      </p>
    </DeleteModal>
    <DateOverrideModal
      v-if="addOverrideScheduleId"
      :save="body => handleSaveOverride(addOverrideScheduleId, body)"
      @close="addOverrideScheduleId = null"
    />
    <DeleteModal
      v-if="overrideToDelete"
      :delete-name="
        overrideToDelete.override.reason ||
        formatDate(overrideToDelete.override.override_date)
      "
      :is-loading="isDeletingOverride"
      @confirm="confirmDeleteOverride"
      @close="overrideToDelete = null"
    />

    <p v-if="isLoading" class="text-sm text-slate-400">
      {{ $t('REAL_ESTATE.SCHEDULES.LOADING') }}
    </p>
    <p v-else-if="!schedules.length" class="text-sm text-slate-400">
      {{ $t('REAL_ESTATE.SCHEDULES.EMPTY') }}
    </p>
    <div v-else class="space-y-3">
      <div
        v-for="schedule in schedules"
        :key="schedule.schedule_id"
        class="border border-slate-200 dark:border-slate-700 rounded-lg p-3"
      >
        <div class="flex items-center justify-between mb-1">
          <p class="text-sm font-medium text-slate-900 dark:text-slate-100">
            {{ schedule.name }}
          </p>
          <div class="flex items-center gap-1 shrink-0">
            <woot-button
              variant="clear"
              size="small"
              icon="edit"
              @click="openEditModal(schedule)"
            />
            <woot-button
              variant="clear"
              size="small"
              color-scheme="alert"
              icon="delete"
              @click="scheduleToDelete = schedule"
            />
          </div>
        </div>
        <p class="text-xs text-slate-400 mb-1">
          {{ hoursTextFor(schedule.working_hours) }}
        </p>
        <p class="text-xs text-slate-500 dark:text-slate-400 mb-2">
          {{ $t('REAL_ESTATE.SCHEDULES.AGENTS_LABEL') }}
          {{
            schedule.agents.length
              ? schedule.agents.map(a => a.name).join(', ')
              : $t('REAL_ESTATE.SCHEDULES.NO_AGENTS_ON_SCHEDULE')
          }}
        </p>

        <div class="pt-2 border-t border-slate-100 dark:border-slate-800">
          <div class="flex items-center justify-between mb-1">
            <p class="text-xs font-medium text-slate-700 dark:text-slate-200">
              {{
                $t('REAL_ESTATE.SCHEDULES.OVERRIDES_LABEL', {
                  count: overridesFor(schedule.schedule_id).length,
                })
              }}
            </p>
            <woot-button
              variant="clear"
              size="small"
              icon="add"
              @click="addOverrideScheduleId = schedule.schedule_id"
            >
              {{ $t('REAL_ESTATE.SCHEDULES.ADD_OVERRIDE_BUTTON') }}
            </woot-button>
          </div>
          <p
            v-if="isLoadingOverridesFor(schedule.schedule_id)"
            class="text-xs text-slate-400"
          >
            {{ $t('REAL_ESTATE.OVERRIDES.LOADING') }}
          </p>
          <ul
            v-else-if="overridesFor(schedule.schedule_id).length"
            class="space-y-1"
          >
            <li
              v-for="override in overridesFor(schedule.schedule_id)"
              :key="override.id"
              class="flex items-center gap-3 text-xs text-slate-700 dark:text-slate-200"
            >
              <span class="w-20 shrink-0">
                {{ formatDate(override.override_date) }}
              </span>
              <span class="w-24 shrink-0">
                {{
                  override.closed
                    ? $t('REAL_ESTATE.OVERRIDES.CLOSED_LABEL')
                    : `${override.start_time}–${override.end_time}`
                }}
              </span>
              <span class="flex-1 text-slate-400 truncate">
                {{ override.reason }}
              </span>
              <woot-button
                variant="clear"
                size="small"
                color-scheme="alert"
                icon="delete"
                @click="
                  overrideToDelete = {
                    scheduleId: schedule.schedule_id,
                    override,
                  }
                "
              />
            </li>
          </ul>
        </div>
      </div>
    </div>
  </SectionCard>
</template>
